import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isPlatformChromeTenant } from '../../../app/platformChromeTenant.js';
import { useApiClient } from '../../../shared/providers/AppDataProvider.js';
import { useRuntimeConfig } from '../../../shared/providers/RuntimeConfigProvider.js';
import { useCustomerSession } from '../../../shared/session/CustomerSessionProvider.js';
import { ACCOUNT_ME_STALE_MS } from '../../../shared/session/fetchPlatformMe.js';
import { useTenantCode } from '../../../shared/tenant/TenantProvider.js';
import {
  fetchAccountPaymentByPaymentId,
  isAccountAccessUsable,
} from '../accountApi.js';
import type { PaymentHistoryItemDTO } from '../accountTypes.js';

function parseTransactionId(
  transactionId: number | string | null | undefined,
): number | null {
  if (transactionId === undefined || transactionId === null) {
    return null;
  }
  if (typeof transactionId === 'number') {
    return Number.isFinite(transactionId) ? transactionId : null;
  }
  const trimmed = transactionId.trim();
  if (trimmed.length === 0) {
    return null;
  }
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

/**
 * Resolve receipt metadata for order detail — prefer list-card row, else
 * paginated payments lookup by paymentId and/or transactionId.
 *
 * `listReceiptRow` semantics (G2):
 * - `undefined` — card not on list / unknown → fetch
 * - `null` — card found, no receipt → do not fetch
 * - DTO — use as-is
 */
export function useOrderReceiptRow(
  paymentId: string | null,
  listReceiptRow: PaymentHistoryItemDTO | null | undefined,
  transactionId: number | string | null = null,
): PaymentHistoryItemDTO | null {
  const api = useApiClient();
  const config = useRuntimeConfig();
  const tenantCode = useTenantCode();
  const { session } = useCustomerSession();
  const customerId = session?.customerId;
  const accessUsable = isAccountAccessUsable(session);
  const platformChrome = isPlatformChromeTenant(tenantCode);
  const parsedTxId = parseTransactionId(transactionId);
  const usablePaymentId =
    paymentId !== null && paymentId.trim().length > 0 ? paymentId.trim() : null;

  const shouldFetch =
    listReceiptRow === undefined &&
    (usablePaymentId !== null || parsedTxId !== null) &&
    customerId !== undefined &&
    accessUsable;

  const lookupQuery = useQuery({
    queryKey: [
      'customer',
      'account',
      customerId ?? -1,
      'paymentLookup',
      usablePaymentId,
      parsedTxId,
    ] as const,
    queryFn: ({ signal }) =>
      fetchAccountPaymentByPaymentId({
        apiBaseUrl: config.apiBaseUrl,
        platformChrome,
        api,
        paymentId: usablePaymentId,
        transactionId: parsedTxId,
        signal,
      }),
    enabled: shouldFetch,
    staleTime: ACCOUNT_ME_STALE_MS,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  return useMemo(() => {
    if (listReceiptRow !== undefined && listReceiptRow !== null) {
      return listReceiptRow;
    }
    if (listReceiptRow === null) {
      return null;
    }
    if (!shouldFetch || lookupQuery.data === undefined) {
      return null;
    }
    return lookupQuery.data;
  }, [listReceiptRow, lookupQuery.data, shouldFetch]);
}
